// Filter out Vite HMR update logs from console
if (import.meta.env.DEV) {
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalInfo = console.info;
  
  const shouldSuppress = (message: string): boolean => {
    return (
      message.includes('[vite]') ||
      message.includes('hot updated') ||
      message.includes('invalidate') ||
      message.includes('Fast Refresh')
    );
  };
  
  console.log = (...args: any[]) => {
    const message = args.join(' ');
    if (!shouldSuppress(message)) {
      originalLog.apply(console, args);
    }
  };
  
  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    if (!shouldSuppress(message)) {
      originalWarn.apply(console, args);
    }
  };
  
  console.info = (...args: any[]) => {
    const message = args.join(' ');
    if (!shouldSuppress(message)) {
      originalInfo.apply(console, args);
    }
  };
}

// Import CSS first to ensure it loads before React renders
import "./styles/tailwind.css";
import "./styles/index.css";

import { createRoot } from "react-dom/client";
import App from "./App";

// Wait for stylesheets to load before rendering to prevent layout shift
const waitForStylesheets = (): Promise<void> => {
  return new Promise((resolve) => {
    // Check if stylesheets are already loaded
    if (document.styleSheets.length > 0) {
      // Small delay to ensure styles are applied
      requestAnimationFrame(() => {
        setTimeout(resolve, 10);
      });
      return;
    }

    // Wait for at least one stylesheet to load
    const checkStylesheets = () => {
      if (document.styleSheets.length > 0) {
        requestAnimationFrame(() => {
          setTimeout(resolve, 10);
        });
      } else {
        setTimeout(checkStylesheets, 50);
      }
    };

    // Start checking after a short delay
    setTimeout(checkStylesheets, 10);

    // Fallback: resolve after max 500ms
    setTimeout(resolve, 500);
  });
};

// Wait for stylesheets, then render
waitForStylesheets().then(() => {
  const container = document.getElementById("root");

  if (!container) {
    throw new Error("Root element not found");
  }

  const root = createRoot(container);

  // Render the app
  root.render(<App />);
});

// Function to hide loader and show content
const hideLoader = () => {
  const loader = document.getElementById("initial-loader");
  if (loader) {
    loader.style.opacity = "0";
    loader.style.visibility = "hidden";
  }
  
  // Mark body as ready to show React content
  document.body.classList.add("react-ready");
  
  // Remove loader from DOM after transition
  if (loader) {
    setTimeout(() => {
      loader.remove();
    }, 300); // Match CSS transition duration
  }
};

// Wait for both React to render AND settings to load
let reactRendered = false;
let settingsLoaded = false;

const checkReady = () => {
  if (reactRendered && settingsLoaded) {
    // Small delay to ensure colors are applied
    requestAnimationFrame(() => {
      setTimeout(hideLoader, 50);
    });
  }
};

// Mark React as rendered after a short delay
requestAnimationFrame(() => {
  setTimeout(() => {
    reactRendered = true;
    checkReady();
  }, 50);
});

// Listen for settings loaded event
window.addEventListener('settings-loaded', () => {
  settingsLoaded = true;
  checkReady();
}, { once: true });

// Fallback: Show content after max 2 seconds even if settings don't load
setTimeout(() => {
  if (!settingsLoaded) {
    settingsLoaded = true;
    checkReady();
  }
}, 2000);